import React, { useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";

import { TextField } from "components/Form";

import { useUpdateBankAccount } from "api/payments/index.js";

/**
 * RenameBankAccountModal - Component to conditionally display a modal that allows the ability to rename a bank account.
 * @param {RenameBankAccountModalProps} props
 * @param {Boolean} props.isOpen - Dictates whether modal is open or closed.
 * @param {Function} props.onClose - function to close modal.
 * @param {Object} props.bankAccount - The bank account that this component is responsible for.
 * @param {Function} props.onComplete - callback function to handle success case.
 * @returns JSX.Element
 */
const RenameBankAccountModal = ({ isOpen, onClose, bankAccount, onComplete }) => {
  const { formatMessage } = useIntl();
  const {
    mutateAsync: updateBankAccount,
    isSuccess: isUpdateBASuccess,
    isLoading: isUpdatingBA,
    error: updateBAError,
  } = useUpdateBankAccount();

  const toast = useToast();

  const handleSubmit = async ({ displayString }) => {
    updateBankAccount({ bankAccountId: bankAccount.bank_account_id, displayString });
  };

  useEffect(() => {
    if (isUpdateBASuccess) {
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "Bank account successfully renamed",
          description: "Popup description",
        }),
        status: "success",
        isClosable: true,
      });
      onComplete();
      onClose();
    }
  }, [isUpdateBASuccess, onComplete, toast, onClose, formatMessage]);

  useEffect(() => {
    if (updateBAError) {
      toast({
        title: formatMessage({
          defaultMessage: "Error",
          description: "Popup title",
        }),
        description: updateBAError.message,
        status: "error",
        isClosable: true,
      });
    }
  }, [updateBAError, toast, formatMessage]);
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <Formik
        initialValues={{ displayString: bankAccount.display_string }}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          displayString: Yup.string()
            .max(
              36,
              formatMessage({
                defaultMessage: "New name must be 36 characters or less",
                description: "Input error message",
              })
            )
            .required(
              formatMessage({
                defaultMessage: "New name cannot be empty",
                description: "Input error message",
              })
            ),
        })}
      >
        {({ isValid, values }) => (
          <Form>
            <ModalContent>
              <ModalHeader>
                <FormattedMessage defaultMessage="Rename Bank Account" description="Modal header" />
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <TextField
                  name="displayString"
                  label={formatMessage({
                    defaultMessage: "New name",
                    description: "Input label",
                  })}
                  maxLength="36"
                />
              </ModalBody>
              <ModalFooter justifyContent="flex-end">
                <Button
                  type="submit"
                  isDisabled={isUpdatingBA || !isValid || !values.displayString.trim()}
                >
                  <FormattedMessage defaultMessage="Submit" description="button label" />
                </Button>
              </ModalFooter>
            </ModalContent>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default RenameBankAccountModal;
